import { useEffect } from "react";
import { Outlet, NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertOctagon, FileText, History, LayoutDashboard, LogOut, ScrollText, ShieldAlert, UserCog, Users, Users2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { AdminProvider, useAdmin } from "./useAdmin";
import Wordmark from "@/components/lux/Wordmark";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, perm: "dashboard.view", end: true, testId: "nav-overview" },
  { to: "/admin/cases", label: "Moderation", icon: AlertOctagon, perm: "cases.view", testId: "nav-cases" },
  { to: "/admin/appeals", label: "Appeals", icon: ShieldAlert, perm: "appeals.review", testId: "nav-appeals" },
  { to: "/admin/accounts", label: "Accounts", icon: Users, perm: "accounts.view_masked", testId: "nav-accounts" },
  { to: "/admin/verifications", label: "Verifications", icon: FileText, perm: "verifications.view", testId: "nav-verifications" },
  { to: "/admin/terms", label: "Banned terms", icon: ScrollText, perm: "terms.manage", testId: "nav-terms" },
  { to: "/admin/staff", label: "Staff", icon: UserCog, perm: "staff.manage", testId: "nav-staff" },
  { to: "/admin/audit", label: "Audit log", icon: History, perm: "audit.view", testId: "nav-audit" },
];

function Shell() {
  const { me, isAuthenticated, isLoading, can } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();
  const client = useQueryClient();

  useEffect(() => {
    document.documentElement.classList.remove("dark");
    document.documentElement.classList.add("admin-theme");
    return () => {
      // leaving admin area → restore dark root for public/advertiser screens
      document.documentElement.classList.remove("admin-theme");
      document.documentElement.classList.add("dark");
    };
  }, []);

  const isAuthPage = location.pathname.startsWith("/admin/login");

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated && !isAuthPage) {
      navigate("/admin/login", { replace: true });
    } else if (isAuthenticated && isAuthPage) {
      navigate("/admin", { replace: true });
    }
  }, [isLoading, isAuthenticated, isAuthPage, location.pathname, navigate]);

  const logout = async () => {
    try {
      await api.post("/admin/auth/logout");
    } catch (_) {}
    client.clear();
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  if (isAuthPage) {
    return (
      <div className="min-h-screen bg-admin-bg text-admin-text">
        <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-5 py-10">
          <Link to="/" className="flex flex-col items-center self-center text-admin-text" data-testid="admin-auth-wordmark">
            <Wordmark size="text-4xl" to={null} tone="dark" />
            <span className="mt-2 block text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-admin-muted">Staff console</span>
          </Link>
          <div className="mt-8">
            <Outlet />
          </div>
          <p className="mt-10 text-center text-xs text-admin-muted">
            <Link to="/" className="underline decoration-admin-muted/50 underline-offset-4 hover:text-admin-text" data-testid="admin-auth-back-link">Back to the public site</Link>
          </p>
        </div>
      </div>
    );
  }

  if (isLoading || !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-admin-bg text-sm text-admin-muted" data-testid="admin-loading">
        Loading staff console...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-admin-bg text-admin-text">
      <header className="sticky top-0 z-30 border-b border-admin-border bg-admin-surface">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2" data-testid="admin-header-wordmark">
              <Wordmark size="text-xl" to={null} tone="dark" />
              <span className="adm-pill border-admin-border bg-admin-surface2 text-admin-muted">Staff</span>
            </Link>
            <span className="adm-pill border-[#E9D3A6] bg-[#FFF8EB] text-[#7A520B]" data-testid="admin-env-badge">Prototype</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-admin-muted">Signed in as</p>
              <p className="text-sm text-admin-text" data-testid="admin-header-name">{me?.name} <span className="text-admin-muted">· {me?.role_label}</span></p>
            </div>
            <button onClick={logout} className="adm-btn adm-btn-outline" data-testid="admin-logout-button" aria-label="Sign out">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>
      <div className="mx-auto flex max-w-[1400px] gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <aside className="hidden w-56 shrink-0 lg:block">
          <nav aria-label="Admin sections" className="sticky top-20 flex flex-col gap-1" data-testid="admin-nav">
            {NAV.filter((n) => can(n.perm)).map(({ to, label, icon: Icon, end, testId }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 rounded-[8px] px-3 py-2 text-sm transition-colors ${
                    isActive ? "bg-[#111827] text-white" : "text-admin-text hover:bg-admin-surface2"
                  }`
                }
                data-testid={`admin-${testId}`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="min-w-0 flex-1">
          <nav className="-mx-4 mb-5 flex gap-1 overflow-x-auto px-4 lg:hidden" aria-label="Admin" data-testid="admin-nav-mobile">
            {NAV.filter((n) => can(n.perm)).map(({ to, label, end, testId }) => (
              <NavLink key={to} to={to} end={end}
                className={({ isActive }) =>
                  `shrink-0 rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                    isActive ? "border-[#111827] bg-[#111827] text-white" : "border-admin-border bg-admin-surface text-admin-text"
                  }`}
                data-testid={`admin-mobile-${testId}`}>
                {label}
              </NavLink>
            ))}
          </nav>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <AdminProvider>
      <Shell />
    </AdminProvider>
  );
}
